import { mountKeypad } from './keypad'
import { isTouchDevice, setRootFlag, trackCanvasBox, trackViewportHeight } from './layout'
import { Connection, createRoom, roomExists } from './net'
import { Renderer } from './render'
import { teamColor, type ServerMsg, type ShotMsg, type StateMsg } from './types'

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id)
  if (!el) throw new Error(`в разметке нет элемента #${id}`)
  return el as T
}

const NAME_KEY = 'mathwar.name'

const menu = $('screen-menu')
const lobby = $('screen-lobby')
const gameScreen = $('screen-game')

const nameInput = $<HTMLInputElement>('name-input')
const codeInput = $<HTMLInputElement>('code-input')
const createBtn = $<HTMLButtonElement>('create-btn')
const joinBtn = $<HTMLButtonElement>('join-btn')
const menuError = $('menu-error')

const lobbyCode = $('lobby-code')
const lobbyPlayers = $('lobby-players')
const soldierSelect = $<HTMLSelectElement>('soldier-select')
const startBtn = $<HTMLButtonElement>('start-btn')
const lobbyHint = $('lobby-hint')
const leaveLobbyBtn = $<HTMLButtonElement>('leave-lobby-btn')

const canvas = $<HTMLCanvasElement>('field')
const canvasBox = $('canvas-box')
const fnInput = $<HTMLInputElement>('fn-input')
const fireBtn = $<HTMLButtonElement>('fire-btn')
const keypad = $('keypad')
const chatLog = $('chat-log')
const chatInput = $<HTMLInputElement>('chat-input')
const playerList = $('player-list')
const turnLabel = $('turn-label')
const timerLabel = $('timer-label')
const statusLabel = $('status-label')
const gameCode = $('game-code')
const showNextToggle = $<HTMLInputElement>('shownext-toggle')
const keypadBtn = $<HTMLButtonElement>('keypad-btn')
const chatBtn = $<HTMLButtonElement>('chat-btn')
const rematchBtn = $<HTMLButtonElement>('rematch-btn')
const leaveGameBtn = $<HTMLButtonElement>('leave-game-btn')
const toast = $('toast')

const touch = isTouchDevice()

const renderer = new Renderer(canvas)
let conn: Connection | null = null
let state: StateMsg | null = null
let myID = ''
let turnEndsAt = 0
let toastTimer: number | null = null

let soldierFunctions: Record<string, string> = {}
let functionsMatchSeq = 0
let lastTurnKey = ''

nameInput.value = localStorage.getItem(NAME_KEY) ?? ''

function showScreen(which: 'menu' | 'lobby' | 'game'): void {
  menu.hidden = which !== 'menu'
  lobby.hidden = which !== 'lobby'
  gameScreen.hidden = which !== 'game'
}

let keypadShown = true
let chatShown = false

function setKeypadShown(on: boolean): void {
  keypadShown = on
  setRootFlag('keypad-hidden', !on)
  keypadBtn.setAttribute('aria-pressed', String(on))
  if (touch) fnInput.inputMode = on ? 'none' : 'text'
}

function setChatShown(on: boolean): void {
  chatShown = on
  setRootFlag('chat-open', on)
  chatBtn.setAttribute('aria-pressed', String(on))
  if (on) {
    chatBtn.classList.remove('alert')
    chatLog.scrollTop = chatLog.scrollHeight
  } else if (document.activeElement === chatInput) {
    chatInput.blur()
  }
}

function showToast(text: string): void {
  toast.textContent = text
  toast.classList.add('visible')
  if (toastTimer !== null) clearTimeout(toastTimer)
  toastTimer = window.setTimeout(() => toast.classList.remove('visible'), 4000)
}

function currentName(): string {
  const name = nameInput.value.trim()
  return name === '' ? 'Аноним' : name
}

async function handleCreate(): Promise<void> {
  menuError.textContent = ''
  createBtn.disabled = true
  try {
    const code = await createRoom()
    enterRoom(code)
  } catch (err) {
    menuError.textContent = String(err)
    console.error('создание комнаты не удалось', err)
  } finally {
    createBtn.disabled = false
  }
}

async function handleJoin(): Promise<void> {
  menuError.textContent = ''
  const code = codeInput.value.trim().toUpperCase()
  if (code.length < 3) {
    menuError.textContent = 'Введите код комнаты'
    return
  }
  joinBtn.disabled = true
  try {
    if (!(await roomExists(code))) {
      menuError.textContent = `Комната ${code} не найдена`
      return
    }
    enterRoom(code)
  } catch (err) {
    menuError.textContent = String(err)
    console.error('вход в комнату не удался', err)
  } finally {
    joinBtn.disabled = false
  }
}

function functionMemoryKey(code: string): string {
  return `mathwar.fns.${code}`
}

function loadFunctionMemory(code: string): void {
  soldierFunctions = {}
  functionsMatchSeq = 0
  lastTurnKey = ''

  const raw = sessionStorage.getItem(functionMemoryKey(code))
  if (raw === null) return
  try {
    const parsed = JSON.parse(raw) as { matchSeq?: number; fns?: Record<string, string> }
    soldierFunctions = parsed.fns ?? {}
    functionsMatchSeq = parsed.matchSeq ?? 0
  } catch (err) {
    console.error('память функций повреждена, начинаю с пустой', err)
    sessionStorage.removeItem(functionMemoryKey(code))
  }
}

function rememberFunction(soldier: number, fn: string): void {
  if (!state) return
  soldierFunctions[String(soldier)] = fn
  sessionStorage.setItem(
    functionMemoryKey(state.code),
    JSON.stringify({ matchSeq: state.matchSeq, fns: soldierFunctions }),
  )
}

function enterRoom(code: string): void {
  localStorage.setItem(NAME_KEY, currentName())
  loadFunctionMemory(code)
  location.hash = code
  conn?.close()
  conn = new Connection(code, currentName(), onMessage, onStatus)
  conn.connect()
  lobbyCode.textContent = code
  gameCode.textContent = code
  showScreen('lobby')
}

function leaveRoom(): void {
  conn?.close()
  conn = null
  state = null
  myID = ''
  location.hash = ''
  chatLog.replaceChildren()
  showScreen('menu')
}

function onStatus(status: 'connecting' | 'online' | 'offline', detail?: string): void {
  statusLabel.dataset.status = status
  switch (status) {
    case 'connecting':
      statusLabel.textContent = 'подключение…'
      break
    case 'online':
      statusLabel.textContent = 'на связи'
      break
    case 'offline':
      statusLabel.textContent = detail ? `нет связи: ${detail}` : 'нет связи'
      if (detail) showToast(detail)
      break
  }
}

function onMessage(msg: ServerMsg): void {
  switch (msg.t) {
    case 'welcome':
      myID = msg.you
      break
    case 'state':
      applyState(msg)
      break
    case 'shot':
      renderer.playShot(msg)
      announceShotOutcome(msg)
      break
    case 'chat':
      appendChat(msg.from, msg.text, msg.kind === 'system')
      break
    case 'funcinfo': {
      const name = state?.players.find((p) => p.id === msg.player)?.name ?? '?'
      appendChat(null, `${name} стрелял: ${msg.fn}`, true)
      break
    }
    case 'error':
      showToast(msg.msg)
      break
  }
}

const SHOT_REASONS: Record<ShotMsg['reason'], string> = {
  undefined: 'функция не определена — снаряд взорвался сразу',
  vertical: 'график встал вертикально — снаряд взорвался',
  maxsteps: 'график слишком длинный — снаряд иссяк на полпути',
  obstacle: '',
}

function announceShotOutcome(msg: ShotMsg): void {
  const name = state?.players[msg.player]?.name ?? '?'
  const reason = SHOT_REASONS[msg.reason]
  window.setTimeout(() => {
    if (msg.hits.length > 0) {
      appendChat(null, `${name} поражает цели: ${msg.hits.length}`, true)
    }
    if (reason !== '') {
      appendChat(null, `${name}: ${reason}`, true)
    }
  }, msg.durationMs)
}

function applyState(next: StateMsg): void {
  state = next
  renderer.setState(next)

  if (next.phase === 'lobby') {
    showScreen('lobby')
    renderLobby(next)
  } else {
    showScreen('game')
    renderGame(next)
  }
}

function renderLobby(s: StateMsg): void {
  lobbyCode.textContent = s.code
  soldierSelect.value = String(s.soldiersPerPlayer)

  const isHost = s.host === myID
  soldierSelect.disabled = !isHost
  startBtn.disabled = !isHost

  const t1 = s.players.filter((p) => p.team === 1).length
  const t2 = s.players.filter((p) => p.team === 2).length
  lobbyHint.textContent = isHost
    ? t1 === 0 || t2 === 0
      ? 'В каждой команде нужен хотя бы один игрок'
      : 'Можно начинать'
    : 'Матч начинает создатель комнаты'

  lobbyPlayers.replaceChildren()
  for (const p of s.players) {
    const row = document.createElement('div')
    row.className = 'lobby-player'
    row.style.setProperty('--team', teamColor(p.team))

    const dot = document.createElement('span')
    dot.className = 'dot'

    const name = document.createElement('span')
    name.className = 'name'
    name.textContent = p.name + (p.id === myID ? ' (вы)' : '') + (p.id === s.host ? ' ★' : '')

    const teamBtns = document.createElement('div')
    teamBtns.className = 'team-switch'
    for (const team of [1, 2]) {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.textContent = team === 1 ? 'Слева' : 'Справа'
      btn.className = p.team === team ? 'active' : ''
      btn.disabled = p.id !== myID
      btn.addEventListener('click', () => conn?.send({ t: 'setTeam', team }))
      teamBtns.append(btn)
    }

    row.append(dot, name, teamBtns)
    lobbyPlayers.append(row)
  }
}

function renderGame(s: StateMsg): void {
  gameCode.textContent = s.code
  const myIndex = s.players.findIndex((p) => p.id === myID)
  const turn = s.turn
  const myTurn = turn !== null && turn.player === myIndex && s.phase === 'playing' && !turn.locked

  turnEndsAt = performance.now() + (turn?.remainingMs ?? 0)

  if (s.phase === 'over') {
    turnLabel.textContent =
      s.winner === 0 ? 'Ничья — обе команды уничтожены' : `Победила команда ${s.winner === 1 ? 'слева' : 'справа'}`
    turnLabel.style.color = s.winner === 0 ? '#dfe6e0' : teamColor(s.winner)
  } else if (turn) {
    const player = s.players[turn.player]
    turnLabel.textContent = myTurn ? 'Ваш ход' : `Ход: ${player?.name ?? '?'}`
    turnLabel.style.color = teamColor(player?.team ?? 1)
  }

  if (s.matchSeq !== functionsMatchSeq) {
    functionsMatchSeq = s.matchSeq
    soldierFunctions = {}
    sessionStorage.removeItem(functionMemoryKey(s.code))
  }

  const turnKey = turn === null ? '' : `${s.matchSeq}:${turn.player}:${turn.soldier}`
  if (turnKey !== lastTurnKey) {
    lastTurnKey = turnKey
    if (myTurn && turn !== null) {
      fnInput.value = soldierFunctions[String(turn.soldier)] ?? ''
      if (document.activeElement !== chatInput && (!touch || keypadShown)) {
        fnInput.focus()
        fnInput.setSelectionRange(fnInput.value.length, fnInput.value.length)
      }
    }
  }

  fnInput.disabled = !myTurn
  fireBtn.disabled = !myTurn
  rematchBtn.hidden = !(s.phase === 'over' && s.host === myID)

  playerList.replaceChildren()
  s.players.forEach((p, i) => {
    const row = document.createElement('div')
    row.className = 'player-row'
    row.style.setProperty('--team', teamColor(p.team))
    if (turn?.player === i && s.phase === 'playing') row.classList.add('active')
    if (!p.connected) row.classList.add('offline')

    const name = document.createElement('span')
    name.className = 'name'
    name.textContent = p.name + (p.id === myID ? ' (вы)' : '')

    const alive = document.createElement('span')
    alive.className = 'alive'
    const total = p.soldiers?.length ?? 0
    const left = p.soldiers?.filter((sd) => sd.alive).length ?? 0
    alive.textContent = `${left}/${total}`

    row.append(name, alive)
    playerList.append(row)
  })
}

function appendChat(from: string | null | undefined, text: string, system: boolean): void {
  const line = document.createElement('div')
  line.className = system ? 'chat-line system' : 'chat-line'
  if (!system && from) {
    const author = document.createElement('span')
    author.className = 'author'
    author.textContent = from
    const player = state?.players.find((p) => p.name === from)
    if (player) author.style.color = teamColor(player.team)
    line.append(author, document.createTextNode(': '))
  }
  line.append(document.createTextNode(text))
  chatLog.append(line)
  chatLog.scrollTop = chatLog.scrollHeight
  if (!chatShown) chatBtn.classList.add('alert')
}

function fire(): void {
  const fn = fnInput.value.trim()
  if (fn === '') {
    showToast('Введите функцию')
    return
  }
  const soldier = state?.turn?.soldier
  if (soldier !== undefined) {
    rememberFunction(soldier, fn)
  }
  conn?.send({ t: 'fire', fn })
}

function sendChat(): void {
  const text = chatInput.value.trim()
  if (text === '') return
  conn?.send({ t: 'chat', text })
  chatInput.value = ''
}

function tickTimer(): void {
  const left = Math.max(0, turnEndsAt - performance.now())
  const playing = state?.phase === 'playing' && state.turn !== null
  timerLabel.textContent = playing ? `${Math.ceil(left / 1000)}с` : '—'
  timerLabel.classList.toggle('urgent', playing && left < 10000)
  requestAnimationFrame(tickTimer)
}

createBtn.addEventListener('click', handleCreate)
joinBtn.addEventListener('click', handleJoin)
codeInput.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') void handleJoin()
})
nameInput.addEventListener('change', () => localStorage.setItem(NAME_KEY, currentName()))

soldierSelect.addEventListener('change', () =>
  conn?.send({ t: 'setSoldiers', n: Number(soldierSelect.value) }),
)
startBtn.addEventListener('click', () => conn?.send({ t: 'start' }))
leaveLobbyBtn.addEventListener('click', leaveRoom)
leaveGameBtn.addEventListener('click', leaveRoom)
rematchBtn.addEventListener('click', () => conn?.send({ t: 'rematch' }))

fireBtn.addEventListener('click', fire)
fnInput.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') fire()
})
chatInput.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') sendChat()
})
showNextToggle.addEventListener('change', () => {
  renderer.options.showNext = showNextToggle.checked
})

chatInput.addEventListener('focus', () => setRootFlag('keyboard-open', touch))
chatInput.addEventListener('blur', () => setRootFlag('keyboard-open', false))

keypadBtn.addEventListener('click', () => setKeypadShown(!keypadShown))
chatBtn.addEventListener('click', () => setChatShown(!chatShown))

mountKeypad(keypad, fnInput)
setKeypadShown(true)
setChatShown(false)
renderer.options.showNext = showNextToggle.checked
trackViewportHeight()
trackCanvasBox(canvasBox)
requestAnimationFrame(tickTimer)

const hashCode = location.hash.replace('#', '').toUpperCase()
if (hashCode.length >= 3) {
  codeInput.value = hashCode
  if (sessionStorage.getItem(`mathwar.token.${hashCode}`)) {
    enterRoom(hashCode)
  }
}
